import { StyleSheet, ViewStyle } from 'react-native'

import { isWeb } from '@common/config/env'
import spacings, { DEVICE_HEIGHT, SPACING, SPACING_MD } from '@common/styles/spacings'
import { ThemeProps } from '@common/styles/themeConfig'
import { BORDER_RADIUS_PRIMARY } from '@common/styles/utils/common'
import { TAB_CONTENT_WIDTH } from '@web/constants/spacings'

interface Style {
  bottomSheet: ViewStyle
  modal: ViewStyle
  portalHost: ViewStyle
  dragger: ViewStyle
  backDrop: ViewStyle
}

/**
 * The one rule to know before adding an overlay anywhere in this app:
 *
 * react-native-web renders every `View` with `position: relative; z-index: 0`,
 * so **every View is its own stacking context**. A z-index therefore only ranks
 * a node against its immediate siblings — it cannot lift it past its parent.
 * That splits every surface into two groups:
 *
 *  - **Portalled** (`<Portal hostName="global">`): siblings of the whole app
 *    tree, so their z-indexes genuinely compete with each other.
 *      899/900  BottomSheet backdrop / sheet (this file)
 *      900/901  Dialog, and Select's dropdown menu
 *      999      Toasts (contexts/toastContext/styles.ts)
 *      1000     Tooltip (components/Tooltip)
 *  - **In-tree** (rendered inside a screen): capped by the app container's
 *    z-index of 0, so they always paint BELOW every portalled surface,
 *    whatever number they carry. The big values scattered around screens
 *    (1000, 1200, 9999, 10_000…) only order those nodes against their local
 *    siblings; they are not a global ranking and raising them changes nothing.
 *
 * So: to put something above an open bottom sheet, do NOT invent a bigger
 * number. Either portal it and pick a value in the list above, or — when the
 * new surface replaces the screen the sheet belongs to — dismiss the sheet via
 * `closeAllBottomSheets()` (see openSheetsRegistry). The Sign takeovers in the
 * extension popup use the second option, which is why nothing here needs a slot
 * between 900 and 999: the sheets a takeover opens itself must stay above it.
 */
export const BOTTOM_SHEET_Z_INDEX = 900

const getStyles = (theme: ThemeProps) =>
  StyleSheet.create<Style>({
    bottomSheet: {
      borderTopStartRadius: BORDER_RADIUS_PRIMARY,
      borderTopEndRadius: BORDER_RADIUS_PRIMARY,
      ...spacings.pvMd,
      paddingHorizontal: isWeb ? SPACING_MD : SPACING
    },
    modal: {
      borderBottomEndRadius: BORDER_RADIUS_PRIMARY,
      borderBottomStartRadius: BORDER_RADIUS_PRIMARY,
      maxWidth: TAB_CONTENT_WIDTH,
      width: '100%',
      margin: 'auto'
    },
    dragger: {
      width: 60,
      height: 3,
      borderRadius: 4,
      backgroundColor: theme.secondaryBorder,
      top: 10
    },
    backDrop: {
      width: '100%',
      height: '100%',
      minHeight: DEVICE_HEIGHT,
      position: 'absolute',
      backgroundColor: 'transparent',
      zIndex: BOTTOM_SHEET_Z_INDEX - 1
    },
    portalHost: {
      ...StyleSheet.absoluteFillObject,
      // Web: pin the overlay host to the VIEWPORT, not the document. As a plain
      // `absolute` box it lived in the page's scrollable area, so Modalize's
      // start-of-animation position (the sheet sits ~a screen below the fold
      // before it slides up) stretched the document — measured: scrollHeight
      // 892 -> 1417 and the window auto-scrolled to y=525, then both unwound as
      // the sheet animated in. That page jump is what read as a flash right
      // before every sheet appeared. `fixed` overflow doesn't extend the
      // scrollable area, so nothing moves. Web-only: RN has no 'fixed'.
      ...(isWeb ? ({ position: 'fixed' } as any) : {}),
      // @ts-ignore prop is supported by react-native-web, but missing in types
      pointerEvents: 'none',
      // Lower number than the toasts' zIndex
      zIndex: BOTTOM_SHEET_Z_INDEX,
      elevation: 19
    }
  })

export default getStyles
